import { images } from '../constants';

const services = [
    {
        id: 1,
        image: images.service1,
        title: 'Event Signup',
        content: 'Register for events and purchase tickets through our platform.',
        paragraphs: [
            'Our event signup service offers a seamless and secure way to register for your favorite conferences, seminars, and workshops. Whether you’re attending in-person or virtually, the process is quick and efficient.',
            'With customized registration forms, real-time seat tracking, and instant ticket confirmations, we make sure your signup process is smooth from start to finish. Join thousands of attendees already using our trusted platform.'
        ],
        gallery: [
            images.service1Gallery1,
            images.service1Gallery2
        ],
        features: [
            'Customizable Signup Forms',
            'Instant E-Ticket Delivery',
            'Secure Online Payments',
            'Real-Time Availability Updates',
            'Mobile-Friendly Experience'
        ],
        process: [
            'Choose Your Event from Our Listings',
            'Select Your Ticket Type and Quantity',
            'Fill Out the Registration Form',
            'Make a Secure Payment',
            'Receive Confirmation and E-Ticket via Email'
        ],
        faqs: [
            {
                id: 1,
                heading: 'How do I register for an event?',
                content: 'Simply select the event, choose your ticket, complete the form, and make a secure payment. Your confirmation and e-ticket will be emailed to you instantly.'
            },
            {
                id: 2,
                heading: 'Can I register for multiple events?',
                content: 'Yes, you can sign up for multiple events and manage your bookings from your dashboard.'
            },
            {
                id: 3,
                heading: 'Is my payment information safe?',
                content: 'Absolutely. We use encrypted payment gateways to ensure the safety and privacy of your financial details.'
            },
            {
                id: 4,
                heading: 'Can I cancel or transfer my registration?',
                content: 'Cancellation and transfer options depend on the event organizer’s policy. Please refer to the event details for specific terms.'
            }
        ]
    },
    {
        id: 2,
        image: images.service2,
        title: 'Event Schedule',
        content: 'Access a comprehensive schedule of sessions and events.',
        paragraphs: [
            'Stay informed and organized with our detailed event schedule. From keynote addresses and breakout sessions to networking opportunities, everything is clearly laid out to help you make the most of your time.',
            'Our platform offers real-time updates, session descriptions, and speaker information so you never miss what matters most. Filter sessions by track, category, or day to personalize your experience.'
        ],
        gallery: [
            images.service2Gallery1,
            images.service2Gallery2
        ],
        features: [
            'Full Agenda Overview',
            'Filter by Date, Track, or Room',
            'Real-Time Updates & Changes',
            'Speaker and Session Details',
            'Mobile & Desktop Accessible'
        ],
        process: [
            'Browse the Event Schedule on the Website or App',
            'Filter Sessions Based on Your Interests',
            'Bookmark Your Favorite Talks and Panels',
            'Set Reminders for Upcoming Sessions',
            'Stay Updated with Real-Time Changes'
        ],
        faqs: [
            {
                id: 1,
                heading: 'Where can I view the full event schedule?',
                content: 'You can access the complete schedule on our website or mobile app. It includes all sessions, speakers, and locations.'
            },
            {
                id: 2,
                heading: 'Can I filter the schedule?',
                content: 'Yes, you can filter sessions by category, speaker, day, or room to customize your experience.'
            },
            {
                id: 3,
                heading: 'Will I be notified of schedule changes?',
                content: 'Absolutely. Our system provides real-time updates and optional notifications for any schedule adjustments.'
            },
            {
                id: 4,
                heading: 'Is the schedule available offline?',
                content: 'Yes, the mobile app offers an offline version of the schedule so you can access it anytime.'
            }
        ]
    },
    {
        id: 3,
        image: images.service3,
        title: 'Attendee Networking',
        content: 'Connect with fellow attendees through networking sessions.',
        paragraphs: [
            'Build valuable connections with professionals, speakers, and exhibitors through our dedicated networking features. Whether in-person or online, we make it easy to meet and engage with people who share your interests.',
            'From one-on-one chats to group discussions and virtual lounges, our platform encourages meaningful conversations that can lead to collaborations, partnerships, and career opportunities.'
        ],
        gallery: [
            images.service3Gallery1,
            images.service3Gallery2
        ],
        features: [
            'In-App Messaging and Meeting Requests',
            'Virtual Networking Lounges',
            'AI-Powered Matchmaking',
            'Scheduled Networking Sessions',
            'Attendee Directory with Filters'
        ],
        process: [
            'Create or Update Your Attendee Profile',
            'Browse the Attendee Directory or Join a Networking Session',
            'Send Meeting Requests or Chat with Other Attendees',
            'Participate in Group Discussions or Virtual Lounges',
            'Follow Up with New Connections Post-Event'
        ],
        faqs: [
            {
                id: 1,
                heading: 'How do I connect with other attendees?',
                content: 'You can use the attendee directory, join scheduled networking sessions, or send direct messages through our platform.'
            },
            {
                id: 2,
                heading: 'Are networking sessions virtual or in-person?',
                content: 'Both! We offer virtual lounges and in-person meetups, depending on the event format.'
            },
            {
                id: 3,
                heading: 'Can I schedule meetings in advance?',
                content: 'Yes, you can send meeting requests and schedule chats ahead of time through your dashboard.'
            },
            {
                id: 4,
                heading: 'Is there a way to match with relevant professionals?',
                content: 'Yes, our AI-powered system suggests matches based on your profile, interests, and goals.'
            }
        ]
    },
    {
        id: 4,
        image: images.service4,
        title: 'Speaker Info',
        content: 'Explore profiles of speakers for a better experience.',
        paragraphs: [
            'Get to know the industry leaders and experts speaking at our event. Our speaker profiles offer detailed information about their backgrounds, areas of expertise, and session topics.',
            'Discover keynote speakers, panelists, and workshop facilitators. Learn about their achievements, view past talks, and plan which sessions to attend based on your interests.'
        ],
        gallery: [
            images.service4Gallery1,
            images.service4Gallery2
        ],
        features: [
            'Detailed Speaker Profiles',
            'Session & Topic Overview',
            'Social Media & Website Links',
            'Search & Filter by Expertise',
            'Past Talk Highlights & Media'
        ],
        process: [
            'Browse the Speaker Directory on the Platform',
            'View Individual Profiles and Session Details',
            'Follow Speakers or Add Sessions to Your Agenda',
            'Engage with Speakers via Q&A or Messaging (if enabled)',
            'Attend Their Talks and Continue the Conversation Post-Event'
        ],
        faqs: [
            {
                id: 1,
                heading: 'Where can I find information about the speakers?',
                content: 'You can explore the full list of speakers on our website or app, complete with profiles, session details, and more.'
            },
            {
                id: 2,
                heading: 'Can I follow specific speakers?',
                content: 'Yes, you can bookmark or follow speakers to keep track of their sessions and updates.'
            },
            {
                id: 3,
                heading: 'Are speaker sessions recorded?',
                content: 'Most sessions are recorded and available on-demand after the event, depending on the speaker’s consent.'
            },
            {
                id: 4,
                heading: 'Can I ask questions to speakers during the event?',
                content: 'Yes, many sessions include live Q&A segments, and some allow you to message speakers directly via the platform.'
            }
        ]
    },
    {
        id: 5,
        image: images.service5,
        title: 'Live Access',
        content: 'Join events virtually with live streaming and audience engagement.',
        paragraphs: [
            'Attend your favorite sessions from anywhere with our reliable live access feature. Whether you’re at home or on the go, you can stream keynote talks, workshops, and panels in real-time.',
            'With high-quality video, interactive tools, and multi-device compatibility, our live access system ensures you don’t miss out on valuable content, even if you can’t be there in person.'
        ],
        gallery: [
            images.service5Gallery1,
            images.service5Gallery2
        ],
        features: [
            'High-Quality Live Streaming',
            'Mobile, Tablet & Desktop Support',
            'Live Chat and Polling Features',
            'Replay Access for Missed Sessions',
            'Secure and Private Viewing Links'
        ],
        process: [
            'Register for the Event and Choose Virtual Attendance',
            'Log In to Your Account on Event Day',
            'Access the Live Stream via Your Dashboard',
            'Participate in Q&A, Polls, and Live Chat',
            'Watch Replays After the Event (if available)'
        ],
        faqs: [
            {
                id: 1,
                heading: 'How do I join a session live?',
                content: 'Log in to your event account and go to your dashboard. Click on the live session link to begin streaming.'
            },
            {
                id: 2,
                heading: 'Do I need to install any software?',
                content: 'No installation required. You can access all sessions directly through your browser on any device.'
            },
            {
                id: 3,
                heading: 'Will I be able to interact during the live stream?',
                content: 'Yes, you can participate through live chat, Q&A sessions, and interactive polls during select events.'
            },
            {
                id: 4,
                heading: 'Can I rewatch sessions later?',
                content: 'Yes, recordings of the sessions will be available for registered attendees after the event, depending on availability.'
            }
        ]
    },
    {
        id: 6,
        image: images.service6,
        title: 'Event Materials',
        content: 'Access downloadable presentations, brochures, and resources.',
        paragraphs: [
            'Enhance your event experience with easy access to essential materials. From speaker presentations and workshop handouts to sponsor brochures and event guides, everything is available in one convenient place.',
            'Our platform allows you to download, view, and organize content based on your sessions of interest, so you never miss valuable resources shared during the event.'
        ],
        gallery: [
            images.service6Gallery1,
            images.service6Gallery2
        ],
        features: [
            'Downloadable PDFs and Slide Decks',
            'Session-Specific Materials',
            'Sponsor & Exhibitor Resources',
            'Digital Event Guidebooks',
            'Organized, Searchable Content Library'
        ],
        process: [
            'Log in to Your Event Account',
            'Navigate to the Materials or Resources Section',
            'Browse Available Downloads by Session or Speaker',
            'Click to View or Download Files',
            'Save for Offline Use or Future Reference'
        ],
        faqs: [
            {
                id: 1,
                heading: 'Where can I find event materials?',
                content: 'Materials are available in your event dashboard under the “Resources” or “Downloads” section.'
            },
            {
                id: 2,
                heading: 'Are the materials available before the event?',
                content: 'Some materials may be available in advance, but most are released during or after the session has taken place.'
            },
            {
                id: 3,
                heading: 'Can I share materials with others?',
                content: 'Materials are for registered attendees only. Sharing is restricted unless otherwise stated by the organizers or speakers.'
            },
            {
                id: 4,
                heading: 'What file formats are supported?',
                content: 'We typically provide materials in PDF, PPT, and DOC formats for easy access and download.'
            }
        ]
    }

];

export default services;